import { sendGTMEvent } from "@next/third-parties/google";
import { Check, X, Zap } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import Confetti from "react-confetti";
import useWindowSize from "react-use/lib/useWindowSize";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useI18n } from "@/locales/client";

const SuccessPopup = () => {
  const t = useI18n();
  const [show, setShow] = useState(false);
  const [moreQuestions, setMoreQuestions] = useState(false);
  const { width, height } = useWindowSize();
  const searchParams = useSearchParams();

  const aiFeatures = [
    t("upgradeModal.aiFeatures.0"),
    t("upgradeModal.aiFeatures.1"),
    t("upgradeModal.aiFeatures.2"),
    t("upgradeModal.aiFeatures.3"),
    t("upgradeModal.aiFeatures.4"),
  ];

  useEffect(() => {
    const success = searchParams.get("success");
    const questions = searchParams.get("questions");

    if (success === "true") {
      setShow(true);

      // Send conversion tracking events
      const sessionId = searchParams.get("session_id");
      const planType = searchParams.get("plan_type");
      const amount = searchParams.get("amount");
      const currency = searchParams.get("currency") || "USD";

      if (sessionId) {
        // Check if we've already tracked this session
        const trackedKey = `conversion_tracked_${sessionId}`;
        const alreadyTracked = sessionStorage.getItem(trackedKey) === "true";

        if (!alreadyTracked) {
          // Send purchase event to Google Analytics
          sendGTMEvent({
            event: "purchase_complete",
            transaction_id: sessionId,
            value: amount ? Number.parseFloat(amount) : 0,
            currency: currency.toUpperCase(),
            items: [
              {
                item_id: "premium_upgrade",
                item_name: `StudyGenie Premium ${planType || ""}`.trim(),
                item_category: "subscription",
                quantity: 1,
              },
            ],
          });

          // Mark this session as tracked
          sessionStorage.setItem(trackedKey, "true");
        }
      }
    }

    if (questions === "true") {
      setMoreQuestions(true);
    }
  }, [searchParams]);

  if (!show) return null;

  return (
    <>
      <Confetti
        width={width}
        height={height}
        recycle={false}
        numberOfPieces={500}
        tweenDuration={10000}
        gravity={0.1}
      />
      <Alert className="fixed bottom-4 right-4 w-[420px] bg-green-50 border-green-300 shadow-lg z-50 p-6">
        <div className="relative">
          <AlertTitle className="flex items-center text-green-800 text-lg font-semibold mb-4">
            <Zap className="mr-3 text-green-800" size={24} />
            Welcome to Studygenie Premium!
          </AlertTitle>
          <AlertDescription className="text-green-700">
            <p className="mb-3">
              Congratulations on upgrading! 🎉 You&apos;ve just supercharged
              your learning experience.
            </p>
            {moreQuestions && (
              <p className="mb-3 text-green-600 font-semibold bg-green-100 p-2 rounded-md">
                Your additional questions are being generated now.
              </p>
            )}
            <p className="mb-2 font-semibold">Your new premium features:</p>
            <ul className="space-y-2 text-sm">
              {aiFeatures.map((feature) => (
                <li key={feature} className="flex items-center">
                  <Check
                    size={16}
                    className="mr-2 text-green-500 flex-shrink-0"
                  />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </AlertDescription>
          <button
            type="button"
            onClick={() => setShow(false)}
            className="absolute top-1 right-0 text-green-700 hover:text-green-900 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
      </Alert>
    </>
  );
};

export default SuccessPopup;
